import * as Sentry from "@sentry/react";

/**
 * The SPA's errors to Sentry: uncaught errors, unhandled rejections and the render crashes the
 * ErrorBoundary catches. Off unless VITE_SENTRY_DSN was set for the build. Failed API calls
 * aren't reported here; the backend reports its own 5xx, with the request ID.
 *
 * An event carries the error with its stack, the page's path, the browser and the account ID.
 * It doesn't carry console output (many screens log whole records), query strings, the tokens of
 * verification and password-reset links, or e-mail addresses in messages. Sentry sends nothing
 * while no error happens: its per-visit sessions are off.
 */

const JWT = /\beyJ[\w-]{5,}\.[\w-]{5,}\.[\w-]*/g;
const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9-]+(?:\.[A-Z0-9-]+)*\.[A-Z]{2,}/gi;
// The links whose last path segment is a token (router.js)
const TOKEN_PATH = /\/(validate|reset-password)\/[^/?#]+/g;

export const scrubText = text =>
  typeof text === "string" ? text.replace(JWT, "[token]").replace(EMAIL, "[email]") : text;

/** A URL without its query string and fragment, and without the token of a link. */
export const scrubUrl = url =>
  typeof url === "string"
    ? scrubText(url.split(/[?#]/)[0].replace(TOKEN_PATH, "/$1/[token]"))
    : url;

/** The event as it may leave (beforeSend). */
export function scrubEvent(event) {
  if (event.request) {
    const userAgent = event.request.headers?.["User-Agent"];
    event.request = {
      url: scrubUrl(event.request.url),
      ...(userAgent && { headers: { "User-Agent": userAgent } }),
    };
  }
  for (const exception of event.exception?.values ?? []) {
    exception.value = scrubText(exception.value);
  }
  if (event.message) event.message = scrubText(event.message);
  // Where Sentry puts the serialized value of a rejection that isn't an Error
  delete event.extra;
  return event;
}

/** A breadcrumb as it may leave, or none (beforeBreadcrumb). */
export function scrubBreadcrumb(breadcrumb) {
  if (breadcrumb.category === "console") return null;
  for (const key of ["url", "from", "to"]) {
    if (breadcrumb.data?.[key]) breadcrumb.data[key] = scrubUrl(breadcrumb.data[key]);
  }
  if (breadcrumb.message) breadcrumb.message = scrubText(breadcrumb.message);
  return breadcrumb;
}

/** Starts Sentry when the build has a DSN; whether it did. */
export function initErrorTracking() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return false;
  Sentry.init({
    dsn,
    environment: import.meta.env.PROD ? "production" : "development",
    sendDefaultPii: false,
    integrations: defaults => [
      ...defaults.filter(({ name }) => name !== "BrowserSession" && name !== "Breadcrumbs"),
      Sentry.breadcrumbsIntegration({ console: false }),
    ],
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
  return true;
}

/** Names the signed-in account (its ID) on every event, following the store's `account`. */
export function trackAccount(store) {
  let current;
  const update = () => {
    const id = store.getState().account?.id ?? null;
    if (id === current) return;
    current = id;
    Sentry.setUser(id ? { id: String(id) } : null);
  };
  update();
  store.subscribe(update);
}

/** Reports a render crash the ErrorBoundary caught, which React doesn't pass on. */
export function captureRenderError(error, componentStack, { permissionDenied = false } = {}) {
  Sentry.captureException(error, {
    contexts: { react: { componentStack } },
    tags: { after_permission_error: permissionDenied },
  });
}
